import { IMG } from "../data";
import WaitlistForm from "../components/WaitlistForm";
import FoundingBadge from "../components/FoundingBadge";

const scrollToWaitlist = (e) => {
  e.preventDefault();
  const el = document.getElementById("apartment-waitlist");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const features = [
  { i: "fa-couch", t: "Fully Furnished" },
  { i: "fa-wifi", t: "High-speed WiFi" },
  { i: "fa-utensils", t: "Modern Kitchen" },
  { i: "fa-sun", t: "Private Terrace" },
  { i: "fa-mountain", t: "Mountain Views" },
  { i: "fa-tree", t: "Near Picos de Europa" },
];

export default function TheApartment() {
  return (
    <main data-testid="apartment-page">
      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-end overflow-hidden">
        <div
          className="hero-image hero-drift"
          style={{ backgroundImage: `url(${IMG.apartmentExterior})` }}
        ></div>
        <div className="hero-overlay"></div>
        <div className="container-cu relative z-10 pb-20 pt-32 w-full">
          <div className="max-w-3xl">
            <p
              className="eyebrow eyebrow-light mb-6"
              style={{ color: "var(--gold-soft)" }}
            >
              The Apartment
            </p>
            <h1
              className="heading-display on-hero text-5xl sm:text-6xl lg:text-7xl mb-6"
              style={{ color: "var(--cream)" }}
            >
              Casa <span className="heading-italic" style={{ color: "var(--gold)" }}>Haya</span>
            </h1>
            <p
              className="text-lg sm:text-xl max-w-xl leading-relaxed mb-10"
              style={{ color: "rgba(250,247,242,0.88)" }}
            >
              Your home in Northern Spain. Coming 2026.
            </p>
            <button
              data-testid="apartment-register-cta"
              onClick={scrollToWaitlist}
              className="btn-cream"
            >
              Register Interest <i className="fa-solid fa-arrow-right text-sm"></i>
            </button>
          </div>
        </div>
      </section>

      {/* DESCRIPTION + FEATURES */}
      <section className="section">
        <div className="container-cu grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-7">
            <p className="eyebrow mb-5">A Modern Retreat</p>
            <h2 className="heading-display text-4xl sm:text-5xl mb-8">
              A place to <span className="heading-italic">breathe</span>, rest and feel at home.
            </h2>
            <p className="text-base leading-relaxed mb-6" style={{ color: "var(--brown-soft)" }}>
              A modern, fully furnished apartment in the heart of Northern
              Spain. Designed for guests who value calm, comfort and authentic
              living.
            </p>
            <p className="text-base leading-relaxed mb-10" style={{ color: "var(--brown-soft)" }}>
              Surrounded by mountains, forests and clean air — a place to
              breathe, rest and feel truly at home.
            </p>
            <span className="badge" data-testid="apartment-coming-badge">
              <i className="fa-regular fa-calendar"></i> Coming 2026 · Register your interest
            </span>
          </div>

          <div className="lg:col-span-5">
            <p className="eyebrow mb-5">In the apartment</p>
            <div className="grid grid-cols-2 gap-3">
              {features.map((f, i) => (
                <div
                  key={f.t}
                  className="p-5 rounded-md flex items-start gap-3"
                  style={{
                    background: "var(--white)",
                    border: "1px solid var(--line)",
                  }}
                  data-testid={`feature-${i}`}
                >
                  <i
                    className={`fa-solid ${f.i} mt-1`}
                    style={{ color: "var(--gold-deep)" }}
                  ></i>
                  <span className="text-sm font-medium" style={{ color: "var(--forest)" }}>
                    {f.t}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section className="section" style={{ background: "var(--cream-deep)" }}>
        <div className="container-cu">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <p className="eyebrow mb-3">The Setting</p>
              <h2 className="heading-display text-4xl sm:text-5xl">
                Mountains, interiors & <span className="heading-italic">stillness</span>.
              </h2>
            </div>
            <p className="text-xs uppercase tracking-[0.22em]" style={{ color: "var(--brown-soft)" }}>
              Reference imagery · Northern Spain
            </p>
          </div>

          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12 md:col-span-8 aspect-[16/10] overflow-hidden rounded-md">
              <img src={IMG.picosDeEuropa} alt="Picos de Europa" className="w-full h-full object-cover hover:scale-105 transition-transform duration-1000" />
            </div>
            <div className="col-span-6 md:col-span-4 aspect-square overflow-hidden rounded-md">
              <img src={IMG.interior1} alt="Living room" className="w-full h-full object-cover hover:scale-105 transition-transform duration-1000" />
            </div>
            <div className="col-span-6 md:col-span-4 aspect-square overflow-hidden rounded-md">
              <img src={IMG.interior2} alt="Modern kitchen" className="w-full h-full object-cover hover:scale-105 transition-transform duration-1000" />
            </div>
            <div className="col-span-6 md:col-span-4 aspect-square overflow-hidden rounded-md">
              <img src={IMG.interior3} alt="Bedroom" className="w-full h-full object-cover hover:scale-105 transition-transform duration-1000" />
            </div>
            <div className="col-span-6 md:col-span-4 aspect-square overflow-hidden rounded-md">
              <img src={IMG.greenValley} alt="Green valley" className="w-full h-full object-cover hover:scale-105 transition-transform duration-1000" />
            </div>
            <div className="col-span-12 md:col-span-8 aspect-[16/9] overflow-hidden rounded-md">
              <img src={IMG.terrace} alt="Terrace view" className="w-full h-full object-cover hover:scale-105 transition-transform duration-1000" />
            </div>
          </div>
        </div>
      </section>

      {/* WAITLIST */}
      <section
        id="apartment-waitlist"
        className="section relative overflow-hidden"
        style={{ background: "var(--forest)" }}
      >
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url(${IMG.mistyMountains})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        ></div>
        <div className="container-narrow relative z-10">
          <div className="text-center mb-12">
            <div className="mb-5 flex justify-center">
              <FoundingBadge variant="dark" />
            </div>
            <h2
              className="heading-display text-4xl sm:text-5xl lg:text-6xl mb-6"
              style={{ color: "var(--cream)" }}
            >
              Register <span className="heading-italic" style={{ color: "var(--gold)" }}>Interest</span>
            </h2>
            <p
              className="text-base sm:text-lg max-w-xl mx-auto leading-relaxed"
              style={{ color: "rgba(250,247,242,0.82)" }}
            >
              Stays open in 2026. Be among the first to know when availability is released.
            </p>
          </div>
          <div
            className="rounded-md p-8 sm:p-12"
            style={{
              background: "rgba(250,247,242,0.04)",
              border: "1px solid rgba(250,247,242,0.14)",
              backdropFilter: "blur(8px)",
            }}
          >
            <WaitlistForm source="apartment" variant="dark" />
          </div>
        </div>
      </section>
    </main>
  );
}
